import React, { useCallback, useEffect, useState } from 'react';
import { TrendUp, Lock, ShieldCheck, Activity, ChartLine, Briefcase, Calendar, WarningOctagon, ArrowRight, Eye, ShieldWarning, Info } from 'phosphor-react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '../../lib/api';
import GeniLoader from '../../components/GeniLoader';
import { OptionsProLockedFeature } from '../../components/options/OptionsProLockedFeature';

export default function MarginVolatility() {
  const { isPortuguese } = useOptionsLocale();
  const { isPro, loading: entitlementsLoading } = useOptionsEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/options/margin-volatility');
      setData(response.data);
    } catch (err) {
      console.error('Error fetching Margin x Volatility data:', err);
      setError(isPortuguese ? 'Falha ao carregar os dados de Margem e Volatilidade' : 'Failed to load Margin & Volatility data');
    } finally {
      setLoading(false);
    }
  }, [isPortuguese]);

  useEffect(() => {
    if (entitlementsLoading) return;
    if (!isPro) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [entitlementsLoading, fetchData, isPro]);

  return (
    <OptionsPageShell hideHeader>
      <div className="min-h-[calc(100vh-180px)] w-full py-6 sm:py-8" data-testid="options-margin-volatility-page">
        <div className="w-full max-w-[800px] mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex flex-col gap-3 border-b border-white/5 pb-4" data-testid="options-margin-vol-header">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/45">
                <TrendUp size={14} weight="bold" />
                <span>MARGIN × VOLATILITY</span>
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white flex items-center gap-2" data-typography="heading">
                {isPortuguese ? 'Margem × Volatilidade' : 'Margin × Volatility'}
                {!isPro && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
                    <Lock size={11} weight="fill" /> PRO
                  </span>
                )}
              </h1>
              <p className="text-sm text-white/60">
                {isPortuguese 
                  ? 'Acompanhe a utilização de margem da sua conta em relação à volatilidade implícita atual do Bitcoin.'
                  : 'Margin utilization in the context of current implied volatility.'}
              </p>
              <p className="text-xs text-white/45 mt-1">
                {isPortuguese
                  ? 'O GENI combina IV Rank, DVOL e as gregas da carteira para calcular um nível conservador de exposição à margem.'
                  : 'GENI combines IV Rank, DVOL and portfolio Greeks to calculate a conservative margin exposure level.'}
              </p>
            </div>
          </div>

          {!isPro && !entitlementsLoading ? (
            <OptionsProLockedFeature
              featureKey="MARGIN_VOLATILITY"
              title={isPortuguese ? 'Margin × Volatility' : 'Margin × Volatility'}
              description={isPortuguese
                ? 'Relacione margem, DVOL, IV Rank e estresse das gregas para dimensionar sua exposição com mais contexto.'
                : 'Connect margin, DVOL, IV Rank and Greek stress to size exposure with more context.'}
              testId="options-margin-volatility-pro-lock"
            />
          ) : loading ? (
            <div className="rounded-[24px] border border-white/5 bg-zinc-950/40 p-12 text-center">
              <GeniLoader size="md" />
            </div>
          ) : error ? (
            <div className="rounded-[24px] border border-white/5 bg-zinc-950/40 p-8 text-center text-red-400">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Card Principal: MARGIN & VOLATILITY CONTEXT */}
              <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none">
                <CardContent className="p-6 sm:p-8 space-y-6">
                  
                  {/* Titulo Card */}
                  <div className="pb-4 border-b border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="space-y-1">
                      <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/35">
                        {isPortuguese ? 'INDICADORES PRINCIPAIS' : 'KEY METRICS'}
                      </p>
                      <h2 className="text-lg font-black text-white tracking-tight">
                        {isPortuguese ? 'Utilização de Margem & Risco de Volatilidade' : 'Margin Utilization & Volatility Risk'}
                      </h2>
                    </div>

                    {/* Bloco Compacto de IM/MM */}
                    {data?.im_percent !== null && data?.im_percent !== undefined && (
                      <div className="flex items-center gap-4 min-w-[200px]" data-testid="header-compact-margin-bars">
                        {/* IM Display */}
                        <div className="space-y-1 flex-1">
                          <div className="flex justify-between text-[11px] font-semibold text-white/50">
                            <span>IM</span>
                            <span className="font-mono font-bold text-white/85">{data.im_percent}%</span>
                          </div>
                          <div className="relative h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-[#00E676] rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(data.im_percent, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* MM Display */}
                        <div className="space-y-1 flex-1">
                          <div className="flex justify-between text-[11px] font-semibold text-white/50">
                            <span>MM</span>
                            <span className="font-mono font-bold text-white/85">{data.mm_percent}%</span>
                          </div>
                          <div className="relative h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-[#A855F7] rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(data.mm_percent, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Grid de Métricas */}
                  <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    
                    {/* CURRENT MARGIN */}
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                        {isPortuguese ? 'MARGEM ATUAL' : 'CURRENT MARGIN'}
                      </p>
                      <p className="text-2xl font-black text-white tabular-nums">
                        {data?.im_percent !== null && data?.im_percent !== undefined ? `${data.im_percent}%` : '—'}
                      </p>
                      <p className="text-[10px] text-white/45 leading-none">
                        {isPortuguese ? `IM (MM: ${data?.mm_percent ?? '—'}%)` : `IM (MM: ${data?.mm_percent ?? '—'}%)`}
                      </p>
                    </div>

                    {/* DVOL VOLATILITY */}
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                        VOLATILITY
                      </p>
                      <p className="text-2xl font-black text-[#A855F7] tabular-nums">
                        {data?.dvol_current !== null && data?.dvol_current !== undefined ? data.dvol_current : '—'}
                      </p>
                      <p className="text-[10px] text-white/45 leading-none">
                        DVOL Index
                      </p>
                    </div>

                    {/* GENI IV RANK */}
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                        IV RANK
                      </p>
                      <p className="text-2xl font-black text-[#00E676] tabular-nums">
                        {data?.iv_rank !== null && data?.iv_rank !== undefined ? data.iv_rank : '—'}
                      </p>
                      <p className="text-[10px] text-white/45 leading-none">
                        {isPortuguese ? 'Escala 0-100' : '0-100 Scale'}
                      </p>
                    </div>

                    {/* CONSERVATIVE LEVEL */}
                    <div className="rounded-[16px] border border-[#A855F7]/15 bg-[#A855F7]/5 p-4 space-y-1" data-testid="conservative-level-card">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-[#A855F7] font-extrabold">
                        CONSERVATIVE LEVEL
                      </p>
                      <p className="text-2xl font-black text-[#A855F7] tabular-nums">
                        {data?.conservative_level !== null && data?.conservative_level !== undefined ? `${data.conservative_level}%` : '—'}
                      </p>
                      <p className="text-[10px] text-[#A855F7]/60 leading-none">
                        {isPortuguese ? 'Limite final' : 'Final threshold'}
                      </p>
                    </div>

                    {/* CAPACITY USED */}
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1" data-testid="capacity-used-card">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                        CAPACITY USED
                      </p>
                      <p className="text-2xl font-black text-white tabular-nums">
                        {data?.capacity_used !== null && data?.capacity_used !== undefined ? `${data.capacity_used}%` : '—'}
                      </p>
                      <p className="text-[10px] text-white/45 leading-none truncate">
                        {isPortuguese ? 'Margem / Limite' : 'Margin / Limit'}
                      </p>
                    </div>

                  </div>

                  {/* Detalhamento Adicional do IV Rank */}
                  {data?.iv_rank !== null && (
                    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-xs space-y-2">
                      <div className="flex justify-between text-white/35">
                        <span className="uppercase tracking-wider font-semibold">{isPortuguese ? 'Histórico de 52 Semanas' : '52W Volatility Range'}</span>
                        <span>DVOL BTC</span>
                      </div>
                      <div className="flex items-center justify-between text-white/80 font-mono">
                        <div className="space-y-0.5">
                          <p className="text-[9px] text-white/35 uppercase leading-none">{isPortuguese ? 'Mínimo de 1 ano' : '52W Low'}</p>
                          <p className="font-bold">{data?.low_52w ?? '—'}</p>
                        </div>
                        <div className="w-1/2 relative h-1 bg-white/10 rounded-full mx-4">
                          <div 
                            className="absolute h-3 w-3 bg-[#00E676] border-2 border-zinc-950 rounded-full top-1/2 transform -translate-y-1/2"
                            style={{ left: `${data?.iv_rank ?? 0}%` }}
                          />
                        </div>
                        <div className="space-y-0.5 text-right">
                          <p className="text-[9px] text-white/35 uppercase leading-none">{isPortuguese ? 'Máximo de 1 ano' : '52W High'}</p>
                          <p className="font-bold">{data?.high_52w ?? '—'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Descrição Metodologia */}
                  <div className="pt-4 border-t border-white/5 space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/35">
                      {isPortuguese ? 'CONCEITOS DE GESTÃO DE RISCO' : 'RISK MANAGEMENT METRICS'}
                    </h3>
                    <p className="text-xs text-white/55 leading-relaxed">
                      {isPortuguese 
                        ? 'O DVOL (Índice de Volatilidade da Deribit) mede a volatilidade implícita esperada para os próximos 30 dias para o Bitcoin. O IV Rank posiciona a leitura atual do DVOL em relação ao seu próprio histórico de 52 semanas, variando de 0 a 100. Valores baixos de IV Rank sugerem que a volatilidade implícita está comprimida e com potencial de expansão, um fator importante para mensurar a segurança do tamanho da sua exposição e utilização de margem.'
                        : 'The DVOL (Deribit Volatility Index) measures the expected 30-day implied volatility for Bitcoin. The IV Rank positions the current DVOL relative to its 52W range from 0 to 100. Lower IV Ranks suggest that implied volatility is historically compressed, signaling a potential expansion phase that is critical for evaluating position sizes and margin safety levels.'}
                    </p>
                  </div>

                </CardContent>
              </Card>

              {/* CARD DE GREEK STRESS DE 3 DIAS */}
              <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none" data-testid="greek-stress-card">
                <CardContent className="p-6 sm:p-8 space-y-6">
                  
                  <div className="pb-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-[#A855F7]">
                        {isPortuguese ? 'SIMULAÇÃO DE ESTRESSE DE 3 DIAS' : '3-DAY GREEK STRESS'}
                      </p>
                      <h2 className="text-lg font-black text-white tracking-tight mt-1">
                        {isPortuguese ? 'Greek Stress & Choque de Volatilidade' : 'Greek Stress & Volatility Shock'}
                      </h2>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
                      <Eye size={12} /> {isPortuguese ? 'INFORMATIVO' : 'INFORMATIONAL ONLY'}
                    </span>
                  </div>

                  {data?.greek_stress ? (
                    <div className="space-y-6">
                      
                      {/* Grid de Resultados de Stress */}
                      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                        
                        {/* BTC SHOCK */}
                        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1">
                          <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                            BTC STRESS
                          </p>
                          <p className="text-2xl font-black text-white tabular-nums">
                            ±{data.greek_stress.price_stress_pct}%
                          </p>
                        </div>

                        {/* IV SHOCK */}
                        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-1">
                          <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold">
                            IV STRESS SHOCK
                          </p>
                          <p className="text-2xl font-black text-[#A855F7] tabular-nums">
                            +{data.greek_stress.iv_stress_points} vol pts
                          </p>
                        </div>

                        {/* WORST IMPACT */}
                        <div className="rounded-[16px] border border-red-500/15 bg-red-500/5 p-4 space-y-1">
                          <p className="text-[9px] uppercase tracking-[0.16em] text-red-400 font-extrabold flex items-center gap-1">
                            <ShieldWarning size={11} />
                            WORST GREEK STRESS
                          </p>
                          <p className="text-2xl font-black text-red-400 tabular-nums">
                            {data.greek_stress.worst_stress_pct_equity}% Equity
                          </p>
                          <p className="text-[10px] text-red-400/70 leading-none">
                            {isPortuguese ? `Pior Lado: BTC ${data.greek_stress.worst_side}` : `Worst Side: BTC ${data.greek_stress.worst_side}`}
                          </p>
                        </div>

                      </div>

                      {/* Tabela Comparativa de Cenários */}
                      <div className="rounded-2xl border border-white/5 bg-white/[0.01] overflow-hidden">
                        <div className="grid grid-cols-3 gap-2 p-3 bg-white/[0.02] border-b border-white/5 text-[10px] uppercase font-bold text-white/35 tracking-wider">
                          <span>{isPortuguese ? 'Cenário (3 Dias)' : 'Scenario (3 Days)'}</span>
                          <span className="text-center">{isPortuguese ? 'BTC UP (+2σ)' : 'BTC UP (+2σ)'}</span>
                          <span className="text-right">{isPortuguese ? 'BTC DOWN (-2σ)' : 'BTC DOWN (-2σ)'}</span>
                        </div>
                        <div className="p-3 space-y-2 text-xs font-mono">
                          
                          {/* Choque de Preço */}
                          <div className="flex justify-between border-b border-white/5 pb-2 text-white/70">
                            <span className="font-sans text-white/45">{isPortuguese ? 'Choque de Preço BTC' : 'BTC Price Shock'}</span>
                            <span className="text-center text-emerald-400">+{data.greek_stress.up_scenario.price_shock_pct}%</span>
                            <span className="text-right text-red-400">{data.greek_stress.down_scenario.price_shock_pct}%</span>
                          </div>

                          {/* Impacto Delta/Gamma */}
                          <div className="flex justify-between border-b border-white/5 pb-2 text-white/70">
                            <span className="font-sans text-white/45">{isPortuguese ? 'Impacto Delta + Gamma' : 'Delta + Gamma Impact'}</span>
                            <span className="text-center tabular-nums">{data.greek_stress.up_scenario.price_greek_impact >= 0 ? '+' : ''}{data.greek_stress.up_scenario.price_greek_impact} BTC</span>
                            <span className="text-right tabular-nums">{data.greek_stress.down_scenario.price_greek_impact >= 0 ? '+' : ''}{data.greek_stress.down_scenario.price_greek_impact} BTC</span>
                          </div>

                          {/* Impacto Vega */}
                          <div className="flex justify-between border-b border-white/5 pb-2 text-white/70">
                            <span className="font-sans text-white/45">{isPortuguese ? 'Impacto Vega (Vol Exp)' : 'Vega Impact (Vol Exp)'}</span>
                            <span className="text-center tabular-nums">{data.greek_stress.up_scenario.vega_impact >= 0 ? '+' : ''}{data.greek_stress.up_scenario.vega_impact} BTC</span>
                            <span className="text-right tabular-nums">{data.greek_stress.down_scenario.vega_impact >= 0 ? '+' : ''}{data.greek_stress.down_scenario.vega_impact} BTC</span>
                          </div>

                          {/* Impacto Líquido Total */}
                          <div className="flex justify-between pt-1 font-bold text-sm">
                            <span className="font-sans text-white/80">{isPortuguese ? 'Impacto Líquido Total' : 'Net Total Impact'}</span>
                            <span className={`text-center tabular-nums ${data.greek_stress.up_scenario.impact_pct_equity >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {data.greek_stress.up_scenario.impact_pct_equity}%
                            </span>
                            <span className={`text-right tabular-nums ${data.greek_stress.down_scenario.impact_pct_equity >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {data.greek_stress.down_scenario.impact_pct_equity}%
                            </span>
                          </div>

                        </div>
                      </div>

                      {/* Bloco Informativo de Ajuste de Risco por Gregas (Greek Risk Adjustment) */}
                      <div className="rounded-2xl border border-[#A855F7]/15 bg-[#A855F7]/5 p-4 flex gap-3 items-start" data-testid="greek-risk-panel">
                        <div className="p-1.5 rounded-lg bg-[#A855F7]/10 text-[#A855F7] mt-0.5">
                          <ShieldCheck size={18} weight="bold" />
                        </div>
                        <div className="space-y-1 flex-1">
                          <div className="flex justify-between items-center">
                            <h4 className="text-xs font-black text-white tracking-wide uppercase">
                              {isPortuguese ? 'Ajuste de Risco por Gregas (Greek Risk)' : 'Greek Risk Adjustment'}
                            </h4>
                            <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${data?.greek_risk_level === 'LOW' ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20' : 'bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20'}`}>
                              {data?.greek_risk_level} Risk (-{data?.greek_risk_adjustment ?? 0} p.p.)
                            </span>
                          </div>
                          <p className="text-[11px] text-white/60 leading-relaxed">
                            {isPortuguese
                              ? `Com base no pior cenário simulado de 3 dias (${data.greek_stress.worst_stress_pct_equity}% da Equity), o risco das gregas da sua carteira é classificado como ${data.greek_risk_level}. Isso aplica uma redução de ${data.greek_risk_adjustment} p.p. sobre o seu limite-base de volatilidade, resultando em um Conservative Level de ${data.conservative_level}%.`
                              : `Based on the worst simulated 3-day scenario (${data.greek_stress.worst_stress_pct_equity}% of Equity), your portfolio Greek risk is classified as ${data.greek_risk_level}. This applies a reduction of ${data.greek_risk_adjustment} p.p. to your volatility base-level, resulting in a final Conservative Level of ${data.conservative_level}%.`}
                          </p>
                        </div>
                      </div>

                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-6 text-center text-xs text-white/45 italic">
                        {isPortuguese 
                          ? 'Dados de estresse indisponíveis. Conecte sua conta Deribit ou certifique-se de possuir posições e saldo de margem ativos.'
                          : 'Stress simulation unavailable. Connect your Deribit account or ensure you have active positions and margin balance.'}
                      </div>
                      
                      {/* Caso de Fallback: Explicar que Greek Risk está indisponível */}
                      <div className="rounded-2xl border border-white/5 bg-white/[0.01] p-4 flex gap-3 items-start text-xs text-white/45">
                        <Info size={16} className="text-white/35 mt-0.5" />
                        <p className="leading-relaxed">
                          {isPortuguese
                            ? 'Como os dados de estresse das gregas não estão disponíveis, o Greek Risk Adjustment está classificado como UNAVAILABLE. O limite final do seu Conservative Level herda integralmente o limite-base determinado unicamente pelas condições de volatilidade implícita do mercado, sem penalizações (Adjustment = 0 p.p.).'
                            : 'Since Greek stress metrics are currently unavailable, the Greek Risk Adjustment is classified as UNAVAILABLE. Your final Conservative Level threshold inherits the market volatility base-level without adjustments (Adjustment = 0 p.p.).'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Bloco discreto de Greeks Normalizados/Agregados */}
                  <div className="pt-4 border-t border-white/5 space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/35">
                      {isPortuguese ? 'GREGAS AGREGADAS' : 'AGGREGATED GREEKS'}
                    </h3>
                    <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 font-mono text-xs">
                      
                      {/* Delta */}
                      <div className="bg-white/[0.01] border border-white/5 rounded-xl p-3">
                        <p className="font-sans text-[10px] text-white/35 uppercase leading-none mb-1">Delta total</p>
                        <p className="font-bold text-white tabular-nums">{data?.options_delta !== null && data?.options_delta !== undefined ? data.options_delta : '—'}</p>
                      </div>

                      {/* Gamma */}
                      <div className="bg-white/[0.01] border border-white/5 rounded-xl p-3">
                        <p className="font-sans text-[10px] text-white/35 uppercase leading-none mb-1">Gamma total</p>
                        <p className="font-bold text-white tabular-nums">{data?.options_gamma !== null && data?.options_gamma !== undefined ? data.options_gamma : '—'}</p>
                      </div>

                      {/* Vega */}
                      <div className="bg-white/[0.01] border border-white/5 rounded-xl p-3">
                        <p className="font-sans text-[10px] text-white/35 uppercase leading-none mb-1">Vega total</p>
                        <p className="font-bold text-white tabular-nums">{data?.options_vega !== null && data?.options_vega !== undefined ? data.options_vega : '—'}</p>
                      </div>

                      {/* Theta */}
                      <div className="bg-white/[0.01] border border-white/5 rounded-xl p-3">
                        <p className="font-sans text-[10px] text-white/35 uppercase leading-none mb-1">Theta total</p>
                        <p className="font-bold text-white tabular-nums">{data?.options_theta !== null && data?.options_theta !== undefined ? data.options_theta : '—'}</p>
                      </div>

                    </div>
                    <p className="text-[11px] text-white/45 leading-relaxed pt-1">
                      {isPortuguese
                        ? 'As gregas acima correspondem ao saldo consolidado da sua conta sincronizado da Deribit.'
                        : 'The Greeks above represent your account consolidated totals synced from Deribit.'}
                    </p>
                  </div>

                </CardContent>
              </Card>

              {/* Card Secundário: PORTFOLIO CONTEXT */}
              <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none">
                <CardContent className="p-6 sm:p-8 space-y-6">
                  
                  <div className="pb-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/35">
                        {isPortuguese ? 'ESTRUTURA DA CARTEIRA' : 'PORTFOLIO CONTEXT'}
                      </p>
                      <h2 className="text-lg font-black text-white tracking-tight mt-1">
                        {isPortuguese ? 'Exposição e Estratégias Ativas' : 'Active Exposure & Strategies'}
                      </h2>
                    </div>
                    <span className="text-xs font-bold text-white/40">
                      {isPortuguese ? 'Dados Reais Sincronizados' : 'Synced Real Portfolio Data'}
                    </span>
                  </div>

                  {/* Resumo Quantitativo */}
                  <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-0.5">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Operações Abertas' : 'Open Operations'}</p>
                      <p className="text-xl font-bold text-white tabular-nums">{data?.portfolio_context?.open_operations_count ?? 0}</p>
                    </div>
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-0.5">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">Short Strangles</p>
                      <p className="text-xl font-bold text-white tabular-nums">{data?.portfolio_context?.short_strangle_count ?? 0}</p>
                    </div>
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-0.5">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">Iron Condors</p>
                      <p className="text-xl font-bold text-white tabular-nums">{data?.portfolio_context?.iron_condor_count ?? 0}</p>
                    </div>
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-0.5">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">Long Calls/Puts</p>
                      <p className="text-xl font-bold text-white tabular-nums">
                        {(data?.portfolio_context?.long_call_count ?? 0) + (data?.portfolio_context?.long_put_count ?? 0)}
                      </p>
                    </div>
                  </div>

                  {/* Pernas do Portfólio */}
                  <div className="grid gap-4 md:grid-cols-2 pt-2">
                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
                      <p className="text-xs font-black text-white/70 tracking-wide uppercase">{isPortuguese ? 'Pernas de Opções' : 'Option Legs'}</p>
                      <div className="space-y-1.5 text-xs text-white/60">
                        <div className="flex justify-between">
                          <span>{isPortuguese ? 'Pernas Short (Vendidas)' : 'Short Legs'}</span>
                          <span className="font-bold text-red-400 tabular-nums">{data?.portfolio_context?.options_short_count ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{isPortuguese ? 'Pernas Long (Compradas)' : 'Long Legs'}</span>
                          <span className="font-bold text-emerald-400 tabular-nums">{data?.portfolio_context?.options_long_count ?? 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
                      <p className="text-xs font-black text-white/70 tracking-wide uppercase">{isPortuguese ? 'Vencimentos Ativos (DTE)' : 'Active Expirations'}</p>
                      {data?.portfolio_context?.expirations_count > 0 ? (
                        <div className="space-y-1 text-xs text-white/60">
                          <div className="flex justify-between">
                            <span>{isPortuguese ? 'Vencimentos Únicos' : 'Unique Expirations'}</span>
                            <span className="font-bold text-white/80 tabular-nums">{data?.portfolio_context?.expirations_count}</span>
                          </div>
                          <p className="text-[10px] text-white/40 truncate font-mono mt-1">
                            {data?.portfolio_context?.expirations.join(', ')}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-white/45 italic">
                          {isPortuguese ? 'Nenhum vencimento ativo identificado.' : 'No active expirations identified.'}
                        </p>
                      )}
                    </div>
                  </div>

                </CardContent>
              </Card>

            </div>
          )}

        </div>
      </div>
    </OptionsPageShell>
  );
}
